/* The Volume Trigger codes and their display labels. Shared so the editable
   journey grid and the read-only rule detail dialog cannot drift apart; the
   codes are what rail.business_rule_ods.volume_trigger stores. */
export const VOLUME_TRIGGER_OPTIONS = [
    {id: 'None', name: 'None'},
    {id: 'Under_Pct', name: 'Underconstrained'},
    {id: 'Under_Count', name: 'Underselling'},
    {id: 'Over_Pct', name: 'Overconstrained'},
    {id: 'Over_Count', name: 'Overselling'}
];

export class BaseRule {
    updatedBy?: string;
    businessRuleID: string;
    changeType?: 'delete' | 'update' | 'add' | 'clone' | null;
    isDraft?: boolean;
    lastUpdatedDate?: string | null;
}

export class BaseChildRule extends BaseRule {
    constructor(public id: string) {
        super();
    }
}

export class ParentBusinessRule extends BaseRule {
    public name: string;
    public journeyType: string[];
    public dayOfWeek: string[];
    public exclusionDate: any[];
    public isDraft: boolean;
    public startDate: Date | string;
    public expireDate: Date |string;
    public bucketRules: ChildBucketRule[];
    public journeyRules: ChildJourneyRule[];
    public dowIDs: {id: string, value: string}[];
    public jtIDs: {id: string, value: string}[];
    public expanded?: boolean;
    public note: string;

    constructor(public businessRuleID: string) {
        super();
        this.bucketRules = [];
        this.journeyRules = [];
        this.journeyType = [];
        this.dayOfWeek = [];
        this.exclusionDate = [];
    }
}

export class ChildBucketRule extends BaseChildRule {
    public class: string;
    public rule: string;
    public daysPrior: number;
    public bucketCode: string;
    public minAlloc: number;
    minAllocDisabled: boolean;
    // 'count' | 'percentage'. Defaults to 'count' server-side for every
    // rule saved before RLP-4314, so absence here means count mode.
    public minAllocType: string;
    public minAllocPct: number;
    public maxAlloc: number;
    maxAllocDisabled: boolean;
}

export class ChildJourneyRule extends BaseChildRule {
    public origin: string;
    public destination: string;
    public rsid: string[];
    public rowIdRsid: {id: string, value: string}[];
    public odChange?: boolean;
    // 'None' | 'Under_Pct' | 'Under_Count' | 'Over_Pct' | 'Over_Count'.
    // The API maps 'None' to a NULL volume_trigger column and back.
    public volumeTrigger: string;
    public volumeFactor: number;
    // Optional override of the automatically chosen critical leg. Both null means
    // "use the critical leg"; the grid will not save only one of the pair.
    public triggerLegOrigin: string;
    public triggerLegDestination: string;
}

export class JourneyType {
    public journeyType: string;
    public name: string;

    constructor(item: any) {
        this.journeyType = item.id;
        this.name = item.name;
    }
}

export class DayOfWeekType {
    public dayOfWeek: string;
    public name: string;

    constructor(item: any) {
        this.dayOfWeek = item.id;
        this.name = item.name;
    }
}

export class ExclusionDate {
    public exclusionDate: Date | string | number;
    public name: string;

    constructor(item: any) {
        this.exclusionDate = item.id;
        this.name = item.name;
    }
}

export class PassengerClass {
    public class: string;
    public name: string;

    constructor(item: any) {
        this.class = item.id;
        this.name = item.name;
    }
}

export class JourneyOrigin {
    public origin: string;
    public name: string;

    constructor(item: any) {
        this.origin = item.id;
        this.name = item.name;
    }
}

export class JourneyDestination {
    public destination: string;
    public name: string;

    constructor(item: any) {
        this.destination = item.id;
        this.name = item.name;
    }
}

export class RSID {
    public rsid: string;
    public name: string;

    constructor(item: any) {
        this.rsid = item.id;
        this.name = item.name;
    }
}

export class RuleGridEdits<T extends BaseRule> {
    public added: T[] = [];
    public deleted: T[] = [];
    public updated: T[] = [];

    public add(item: T): void {
        if (!this.added.includes(item)) {
            this.added.push(item);
        }
    }

    public delete(item: T): void {
        if (item.changeType === 'add' || item.changeType === 'clone') {
            this.removeItem(item, this.added);
        } else {
            this.removeItem(item, this.updated);
            if (!this.deleted.includes(item)) {
                this.deleted.push(item);
            }
        }
    }

    public getChangedBusinessRuleIDs(excludeIDs: string[]): string[] {
        const added = this.added.filter(br => !excludeIDs.includes(br.businessRuleID)).map(br => br.businessRuleID);
        const updated = this.updated.filter(br => !excludeIDs.includes(br.businessRuleID)).map(br => br.businessRuleID);
        return [...added, ...updated];
    }

    public hasChanges(): boolean {
        return Boolean(this.added.length || this.deleted.length || this.updated.length);
    }

    public reset(): void {
        this.added = [];
        this.deleted = [];
        this.updated = [];
    }

    public save(item: T, isNew: boolean): void {
        if (isNew) {
            this.add(item);
        } else {
            this.update(item);
        }
    }

    public update(item: T): void {
        if (item.changeType === 'add' || item.changeType === 'clone') {
            this.insertItem(item, this.added);
        } else {
            this.insertItem(item, this.updated);
        }
    }

    private getIndex(item: T, data: T[]): number {
        return data.indexOf(item);
    }

    private insertItem(item: T, data: T[]): void {
        const index = this.getIndex(item, data);
        if (index >= 0) {
            data.splice(index, 1, item);
        } else {
            data.push(item);
        }
    }

    private removeItem(item: T, data: T[]): void {
        const index = this.getIndex(item, data);
        if (index >= 0) {
            data.splice(index, 1);
        }
    }
}

export interface RulePrioritiesJourney {
    journey_date: string;
    journey_rsid: string;
    journey_class: string;
    journey_origin: string;
    journey_destination: string;
    rule_name: string;
}

export interface RulePriority {
    rule_name: string;
    rule_priority: number;
    original_priority?: number;
}

export interface RulePrioritiesResponse {
    journeys: RulePrioritiesJourney[];
    rule_priorities: RulePriority[];
    status: 'success' | 'too many results' | 'conflicting rule priorities' | 'no results found';
}

export interface RuleDefinitionBucket {
    id: string;
    class: string;
    rule: string;
    daysPrior: number;
    bucketCode: string;
    minAlloc: number;
    maxAlloc: number;
    updatedBy: string;
    businessRuleID: string;
}

export interface RuleDefinitionJourney {
    origin: string;
    destination: string;
    rsid: string[];
    updatedBy: string;
    businessRuleID: string;
    volumeTrigger: string;
    volumeFactor: number;
    triggerLegOrigin: string;
    triggerLegDestination: string;
}

export interface RuleDefinition {
    business_rule_name: string;
    exclusion_dates: string[];
    start_date: string;
    expiry_date: string;
    journey_types: string[];
    days_of_week: string[];
    bucket_rules: RuleDefinitionBucket[];
    journey_rules: RuleDefinitionJourney[];
}

export interface RuleDefinitionsResponse {
    rules: RuleDefinition[];
}

/* Impact preview (RLP-4262). The endpoint returns snake_case verbatim; these
   interfaces mirror the API contract rather than the camelCase rule models, so
   nothing has to be re-keyed on the way in. */
export interface ImpactPreviewAsOf {
    /* AOD partition the fares and forecasts were read from. */
    pipeline_created_date: string;
    mv_refreshed_at: string;
    /* Last journey_date the materialised view covers. */
    mv_horizon_end: string;
}

export interface ImpactPreviewScopeGuard {
    /* computation_timeout means the fare recomputation blew its budget on a very
       broad scope; matching and scope are still returned, fare and ballpark are not. */
    status: 'ok' | 'beyond_horizon' | 'computation_timeout';
    evaluated_journey_count: number;
    horizon_exceeded_by_days: number;
    capped: boolean;
}

export interface ImpactPreviewScope {
    description: string;
    journeys_evaluated: number;
}

export interface ImpactPreviewOutrankingRule {
    rule_name: string;
    priority: number;
    journeys: number;
}

export interface ImpactPreviewDisplacedRule {
    rule_name: string;
    journeys: number;
}

/* Present only when the candidate carries a volume trigger. */
export interface ImpactPreviewTrigger {
    evaluated: boolean;
    fires: number;
    of: number;
}

export interface ImpactPreviewMatching {
    candidate_tier: number;
    journeys_governed: number;
    change_winning_rule: number;
    outranked: number;
    /* Set when the rule reaches no journey today: the calendar date its
       days-prior bands first cover one, or a flag that they never can. */
    first_applicable_date?: string | null;
    bands_never_cover?: boolean;
    outranking_rules: ImpactPreviewOutrankingRule[];
    displaced_rules: ImpactPreviewDisplacedRule[];
    trigger?: ImpactPreviewTrigger;
}

/* The LEAD fare -- the price on sale. Drives the hero only, never the ballpark. */
export interface ImpactPreviewFare {
    /* false for Min_Alloc / Max_Alloc: those set seat floors and never move the fare. */
    applies: boolean;
    source: string;
    lead_bucket_moved: number;
    delta_mean_where_moved: number;
    delta_pct_where_moved: number;
    delta_min_where_moved: number;
    delta_max_where_moved: number;
    delta_stddev_where_moved: number;
    excluded_null_fare_journeys: number;
}

/* The LADDER price -- allocation-weighted across the open ladder. Not the same
   price as ImpactPreviewFare, and an order of magnitude smaller in practice. */
export interface ImpactPreviewBallpark {
    base_pax: number;
    base_rev: number;
    fare_pct: number;
    ladder_price_moved: number;
    break_even_loss: number;
    computable: boolean;
    /* True for allocation rules: seats move between buckets with total
       units conserved — no demand response applies, so no judgement table
       and no break-even. */
    mix_shift_only?: boolean;
    /* Demand-response base: only journeys whose ladder price actually moved
       are in play when demand shifts — no stimulus, no response. */
    fare_pct_moved?: number;
    moved_base_pax?: number;
    moved_base_rev?: number;
    /* Sub-rule tabs only: journeys the waterfall could not split between
       rows (unpriceable in an intermediate world); they appear only in the
       Overall figures. */
    unattributed_journeys?: number;
    /* Journeys whose binding Max bucket has no fare: the clamp cannot be
       computed, so they are excluded — never imputed as "no change". */
    excluded_uncapped_journeys?: number;
    /* Journeys whose rule moved seats into an unpriceable bucket — excluded
       from the weighted ladder price and disclosed. */
    excluded_unpriced_shift_journeys?: number;
    not_computable_reason: string | null;
}

export interface ImpactPreviewConflict {
    rule_name: string;
    reason: string;
}

export interface ImpactPreviewConflicts {
    regular: ImpactPreviewConflict[];
    /* filter_out_wildcard_rules auto-passes *ALL rows, so the clean case has to
       disclose the skip rather than claim "no conflicts". */
    wildcard_skipped_count: number | null;
    /* True when the check itself crashed — conflicts were NOT assessed. */
    failed?: boolean;
    /* True when the candidate is a *ALL rule the validator auto-passes. */
    candidate_is_wildcard?: boolean;
}

export type ImpactPreviewEmptyReasonCode =
    'no_departures_for_services'
    | 'exclusions_removed_all_dates'
    | 'dow_never_occurs'
    | 'od_absent_from_network'
    | 'journey_type_never_matches'
    | 'market_has_no_members';

export interface ImpactPreviewEmptyReason {
    code: ImpactPreviewEmptyReasonCode;
    detail: string;
}

/* Per-journey detail. Not in the modal payload -- fetched only for the export. */
export interface ImpactPreviewJourney {
    journey_date: string;
    journey_rsid: string;
    journey_class: string;
    journey_origin: string;
    journey_destination: string;
    incumbent_rule: string;
    incumbent_priority: number;
    candidate_wins: boolean;
    p0: number;
    p1: number;
    delta: number;
    sellable_demand: number;
}

export interface ImpactPreviewCoverage {
    matched: number;
    governed_today: number;
    /* Journeys the days-prior bands merely ADD to the rule's reach as
       departures approach; whether they end up governed then depends on the
       rule book at that time. */
    added_later: number;
    /* Already nearer to departure than any band's inner edge — permanently
       out of this rule's reach. */
    never_covered: number;
    first_entry_date: string | null;
    last_entry_date: string | null;
}

/* Engine-precedence override of one bucket-rule row inside the combined
   rule (journey_profile_generator discards the row on these journeys). */
export interface ImpactPreviewNullified {
    journeys: number;
    of: number;
    by: string[];
    total: boolean;
}

/* One waterfall layer (all rows of a rule type): its contribution to the
   combined rule, measured on top of the layers before it. */
export interface ImpactPreviewSubRule {
    label: string;
    rule_type: string;
    bucket_code: string;
    days_prior: number;
    alloc_value: number | null;
    coverage: ImpactPreviewCoverage;
    nullified: ImpactPreviewNullified | null;
    matching: ImpactPreviewMatching;
    fare: ImpactPreviewFare;
    ballpark: ImpactPreviewBallpark;
    /* A fully-overridden row's own phase: days where its band is engaged
       but no overrider's is. Solo estimate over that window; not part of
       the Overall, which prices each journey at its final state. */
    transient_solo?: {
        days_lo: number;
        days_hi: number;
        start_date: string;
        end_date: string;
        journeys: number;
        fare_pct: number;
        revenue_delta: number;
        mix_shift_only: boolean;
    } | null;
}

export interface ImpactPreviewResponse {
    as_of: ImpactPreviewAsOf;
    scope_guard: ImpactPreviewScopeGuard;
    scope: ImpactPreviewScope;
    matching: ImpactPreviewMatching | null;
    /* Both absent when scope_guard.status is computation_timeout -- the fare step
       is the one that ran out of budget. */
    fare?: ImpactPreviewFare;
    ballpark?: ImpactPreviewBallpark;
    conflicts: ImpactPreviewConflicts | null;
    empty_reasons: ImpactPreviewEmptyReason[];
    journeys_available: number;
    journeys?: ImpactPreviewJourney[];
    /* Whether the assessment was recorded in rail.rule_impact_previews. That table
       is deliberately outside the rule and audit trigger sets, so the footer's
       no-write claim is unaffected and there is no UI for it. */
    preview_recorded?: boolean;
    computed_in_seconds?: number;
    coverage?: ImpactPreviewCoverage;
    /* Adjacent Min bands where the nearer-to-departure floor is cheaper:
       the engine replaces (never maxes) floors per band, so the fare on
       sale will genuinely drop close in. */
    descending_floors?: {class: string; outer: string; inner: string}[];
    sub_rules?: ImpactPreviewSubRule[];
    sub_rules_skipped?: {assessed: number; of: number; reason: string};
    /* Present only on the per-tab merged views the dialog builds. */
    nullified?: ImpactPreviewNullified | null;
    transient_solo?: ImpactPreviewSubRule['transient_solo'];
}

/* Optional narrowing sent from the scope-guard card. Only ever restricts the
   candidate's declared scope -- never widens it -- so fields are omitted when
   the user has not narrowed on them. */
export interface ImpactPreviewScopeRequest {
    start_date?: string;
    end_date?: string;
    markets?: string[];
    passenger_class?: string;
}

/** Which rule beat this one, and on what priority (RLP-4261). */
export interface RuleSuppressedBy {
    ruleName: string;
    effectivePriority: number;
    priorityIsOverridden: boolean;
}

/**
 * Pre-populated search criteria for the business rule prioritization screen. Built
 * server-side, per unapplied rule, because that screen requires exactly one ruleType and
 * a journey's suppressed rules can span more than one.
 */
export interface RuleDeepLink {
    ruleType: string;
    rsid: string;
    passengerClass: string;
    origin: string;
    destination: string;
    startDate: string;
    endDate: string;
    journeyType: string;
}

export interface RankedRule {
    ruleId: string;
    ruleName: string;
    ruleType: string;
    ruleBucket: string;
    ruleBucketOrder: number;
    startDate: string;
    minAlloc: number;
    maxAlloc: number;
    journeyType: string;
    basePriority: number;
    overridePriority: number;
    effectivePriority: number;
    rank: number;
    /** Present only on unapplied rules. */
    suppressedBy?: RuleSuppressedBy;
    deepLink?: RuleDeepLink;
}

export interface JourneyRuleRankingRequest {
    journeyDate: string;
    rsid: string;
    passengerClass: string;
    journeyOrigin: string;
    journeyDestination: string;
}

export interface JourneyRuleRankingResponse {
    status: string;
    journey: JourneyRuleRankingRequest;
    appliedRules: RankedRule[];
    unappliedRules: RankedRule[];
}

export class ValidationResponse {
    public isValid: boolean;
    public pass: {id: string, name: string}[];
    public fail: ValidationFailure[];
    overlapALLtoALLRules: ValidationFailure[];
}

export class ValidationFailure {
    id: string;
    name: string;
    message?: string;
    messages?: string[];
    conflicts?: {id: string, name: string}[];
}

// Per-business-rule failure entry returned by the buckets/journeys/parent_rules
// PATCH endpoints. `reason` is a grouping/label key ('conflict' today) and should
// be treated as an open string rather than an enum, since more reasons may be
// added server-side later.
export interface BusinessRuleSaveFailure {
    businessRuleID: string;
    reason: string;
    message: string;
    lastUpdatedBy?: string;
}

// Response body shape now returned (always with HTTP 200) by the buckets/journeys/
// parent_rules PATCH/PUT/DELETE endpoints that carry a per-rule version check.
// Each business rule in the batch is processed independently, so a single call
// can report a mix of successes and failures. `versions` gives the new
// lastUpdatedDate for every successfully-touched rule -- one "Save" click fires
// several of these requests, and if two touch the same rule, the one that
// commits second must know about the first's bump instead of comparing
// against the value the grid loaded before either ran, or it would report a
// false conflict against the user's own save. See
// BusinessRulesService.saveGridEdits()/applyUpdatedVersions().
// The extra diagnostic booleans some endpoints add (added/deleted/updated/
// jtDeleted/dowDeleted) are informational only and intentionally not modeled here.
export interface BusinessRuleSaveResponse {
    succeeded: string[];
    failed: BusinessRuleSaveFailure[];
    versions?: {[businessRuleID: string]: string};
}

// One merged row in the save-summary popup: a single business rule's outcome
// across all of the PATCH calls made during a save.
export interface SaveSummaryItem {
    businessRuleID?: string;
    name: string;
    success: boolean;
    reason?: string;
    message?: string;
    lastUpdatedBy?: string;
    // Set for a whole-rule deletion so the popup can report "Deleted" rather
    // than "Saved" -- undefined for every other kind of change.
    deleted?: boolean;
}

/* Business rule change history (read-only). The API reconstructs these from the
   rail.audit_business_rule* tables and returns them already grouped, already
   sorted newest-first and already display-formatted: `before`/`after` are the
   strings to render, never codes to resolve, so the dialog needs none of the
   dowData / journeyTypeData lookups the grid uses.

   `section` and `changeType` are documented value sets rather than TypeScript
   enums, for the same reason BusinessRuleSaveFailure.reason is -- the server may
   add values later and the UI must degrade, not fail to compile. */

// 'Rule' | 'Bucket Rules' | 'Journey Rules' | 'Days of Week' | 'Journey Types'
export type RuleHistorySection = string;
// 'Added' | 'Updated' | 'Removed' | 'Restored'
export type RuleHistoryChangeType = string;

export interface RuleHistoryField {
    label: string;
    // null where there was no prior value (an added row).
    before: string | null;
    // null where there is no resulting value (a removed row).
    after: string | null;
}

export interface RuleHistoryEntry {
    section: RuleHistorySection;
    // Human-readable identity of the changed row as it was AT THE TIME, e.g.
    // 'first · Min · 249d · G1'. Built server-side: the client has no copy of
    // the child row as it stood then.
    target: string;
    targetDetail: string | null;
    changeType: RuleHistoryChangeType;
    changedBy: string | null;
    rowId: string;
    // True when the retention purge removed everything before this snapshot, so
    // there was no baseline to diff against and `fields` is empty.
    unknownBaseline: boolean;
    fields: RuleHistoryField[];
}

export interface RuleHistoryChangeSet {
    id: string;
    // 'YYYY-MM-DD HH:mm:ss.ffffff' -- space-separated and timezone-free, which
    // is what DateUtils.convertToDate parses with the 'YYYY-MM-DD HH:mm:ss'
    // format. The audit column is `timestamp without time zone`.
    changedAt: string;
    // null for changes we cannot attribute; rendered as 'Unknown'.
    changedBy: string | null;
    summary: string;
    counts: {added: number, updated: number, removed: number, restored: number};
    entryCount: number;
    isCreation: boolean;
    entries: RuleHistoryEntry[];
}

export interface RuleHistoryResponse {
    businessRuleId: string;
    // null when the live rule row is gone; audit rows outlive their rule.
    ruleName: string | null;
    ruleExists: boolean;
    // Set when the retention purge has removed rule-level history that the
    // child tables still have, which is why some old change sets have no actor.
    parentHistoryTruncated: boolean;
    retentionNote: string | null;
    changeSetsTruncated: boolean;
    // Newest first. Ordering is the server's job -- the dialog does not sort.
    changeSets: RuleHistoryChangeSet[];
}

/* View model: the opener adds the locale-formatted timestamp so the template
   carries no formatting logic, mirroring how inventory.component.ts pre-formats
   OverrideAuditEntry.overrideDateTime before handing it to its dialog. */
export interface RuleHistoryChangeSetView extends RuleHistoryChangeSet {
    changedAtDisplay: string;
}


/* ---- Immediate apply of saved rules (RLP-4281) ---------------------------- */

/* One row of the Save and Apply checklist. Captured BEFORE the save runs, because
   that is the only point at which the pending edit lists still describe what the
   save is about to do; businessRuleID is resolved to the server id afterwards. */
export interface ChangedRuleSummary {
    businessRuleID: string;
    name: string;
    // A rule created in this save has no server id until the save returns.
    isNew: boolean;
    // Ticked in the modal. All ticked rules share one action -- the user cannot
    // review one rule and send another.
    selected: boolean;
}

/* Magnitude of an immediate apply. Deliberately coarse: it conveys scale so the
   user can sanity-check what they are about to push, not an itemised list. */
export interface ApplyPreviewResponse {
    journeys: number;
    ods: number;
    departure_dates: number;
    buckets: number;
    rules: number;
}

export type ApplyMode = 'review' | 'send';

export interface ApplyRequestResponse {
    applyId: string;
    status: string;
}

export interface ApplyJobStatus {
    applyId: string;
    status: string;
    mode: ApplyMode;
    note: string | null;
    affectedCount: number | null;
    // Departure dates the job did not touch, e.g. no optimizer selections on file.
    skippedDates: string[];
    startDate: string | null;
    endDate: string | null;
    requestedDate: string | null;
    completedDate: string | null;
}

/* What the apply modal hands back to the component. */
export interface ApplyDialogResult {
    action: 'discard' | 'skip' | 'review' | 'send';
    brIds: string[];
    startDate: string;
    endDate: string;
}
